/* ==========================================================================
   DreamWood Rings — Theme JS
   Web components. No framework. Uses Shopify's native variant + cart APIs.
   ========================================================================== */

const DW = {
  money(cents) {
    const fmt = window.DWConfig?.moneyFormat || '${{amount}}';
    const v = (cents / 100).toFixed(2);
    const parts = v.split('.');
    const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace('{{amount}}', withCommas + '.' + parts[1])
      .replace('{{amount_no_decimals}}', withCommas)
      .replace('{{amount_with_comma_separator}}', withCommas + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', withCommas);
  },
  toast(msg) {
    let t = document.querySelector('.dw-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'dw-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add('is-in'));
    clearTimeout(t._timer);
    t._timer = setTimeout(() => t.classList.remove('is-in'), 3000);
  },
  trapFocus(container) {
    const sel = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    const nodes = () => Array.from(container.querySelectorAll(sel)).filter(n => n.offsetParent !== null);
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const list = nodes();
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  }
};
window.DW = DW;

/* ---------- Scroll reveal ---------- */
(function () {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('.dw-reveal').forEach(el => el.classList.add('is-in'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  const watch = () => document.querySelectorAll('.dw-reveal:not(.is-in)').forEach(el => io.observe(el));
  watch();
  document.addEventListener('shopify:section:load', watch);
})();

/* ==========================================================================
   Drawer
   ========================================================================== */
class DWDrawer extends HTMLElement {
  connectedCallback() {
    this.scrim = this.querySelector('.dw-drawer__scrim');
    this.panel = this.querySelector('.dw-drawer__panel');
    this.scrim?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-drawer-close]').forEach(b =>
      b.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
  }
  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this._release = DW.trapFocus(this);
    setTimeout(() => {
      this.querySelector('[data-drawer-close]')?.focus();
    }, 60);
  }
  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this._release?.();
    this._opener?.focus();
  }
  toggle(opener) {
    this._opener = opener;
    this.classList.contains('is-open') ? this.close() : this.open();
  }
}
customElements.define('dw-drawer', DWDrawer);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-drawer-open]');
  if (!btn) return;
  const target = document.getElementById(btn.dataset.drawerOpen);
  target?.toggle(btn);
});

/* Mobile nav accordion */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-submenu-toggle]');
  if (!btn) return;
  const sub = btn.parentElement.nextElementSibling;
  const open = sub.classList.toggle('is-open');
  btn.setAttribute('aria-expanded', String(open));
});

/* ==========================================================================
   Accordion
   ========================================================================== */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.dw-acc__btn');
  if (!btn) return;
  const panel = document.getElementById(btn.getAttribute('aria-controls'));
  const open = btn.getAttribute('aria-expanded') === 'true';
  btn.setAttribute('aria-expanded', String(!open));
  if (panel) panel.hidden = open;
});

/* ==========================================================================
   Product Gallery
   ========================================================================== */
class DWGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-gallery-main]');
    this.thumbs = Array.from(this.querySelectorAll('.dw-gallery__thumb'));
    this.thumbs.forEach(t => t.addEventListener('click', () => this.select(t.dataset.mediaId)));
  }
  select(mediaId) {
    if (!mediaId) return;
    const src = this.querySelector(`[data-media-id="${mediaId}"] img`);
    if (src && this.main) {
      const img = this.main.querySelector('img');
      img.src = src.dataset.full || src.src;
      img.srcset = src.dataset.fullSrcset || '';
      img.alt = src.alt || '';
    }
    this.thumbs.forEach(t =>
      t.setAttribute('aria-current', String(t.dataset.mediaId === String(mediaId))));
  }
}
customElements.define('dw-gallery', DWGallery);

/* ==========================================================================
   Variant Picker
   Width -> Size. Uses product JSON for availability; Section Rendering API
   for price/media/buy-button so it stays correct with any Shopify setup.
   ========================================================================== */
class DWVariants extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.form = document.getElementById(this.dataset.form);
    this.idInput = this.form?.querySelector('input[name="id"]');

    const data = this.querySelector('[data-variant-json]');
    this.variants = data ? JSON.parse(data.textContent) : [];

    this.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) this.onChange();
    });

    this.updateAvailability();
  }

  get selected() {
    return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map(i => i.value);
  }

  match() {
    const sel = this.selected;
    return this.variants.find(v =>
      v.options.length === sel.length && v.options.every((o, i) => o === sel[i]));
  }

  /* Disable size chips that don't exist for the chosen width */
  updateAvailability() {
    const groups = Array.from(this.querySelectorAll('[data-option-index]'));
    groups.forEach((group, gi) => {
      if (gi === 0) return; // first option always fully enabled
      const prior = this.selected.slice(0, gi);
      const valid = new Set(
        this.variants
          .filter(v => prior.every((p, i) => v.options[i] === p))
          .filter(v => v.available)
          .map(v => v.options[gi])
      );
      group.querySelectorAll('input[type="radio"]').forEach(input => {
        input.disabled = !valid.has(input.value);
      });
      // If current selection became invalid, move to first valid
      const checked = group.querySelector('input:checked');
      if (checked && checked.disabled) {
        const next = group.querySelector('input:not(:disabled)');
        if (next) next.checked = true;
      }
    });
  }

  async onChange() {
    this.updateAvailability();
    const variant = this.match();

    // Update visible option labels
    this.querySelectorAll('[data-option-index]').forEach((g, i) => {
      const label = g.querySelector('[data-option-selected]');
      if (label) label.textContent = this.selected[i] || '';
    });

    if (!variant) {
      this.setUnavailable();
      return;
    }

    // Native URL update — keeps share/back button honest
    if (this.productUrl) {
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }
    if (this.idInput) this.idInput.value = variant.id;

    // Swap gallery to the variant image
    if (variant.featured_media) {
      document.querySelector('dw-gallery')?.select(variant.featured_media.id);
    }

    await this.renderSection(variant.id);
  }

  setUnavailable() {
    const btn = document.getElementById(`dw-add-${this.sectionId}`);
    if (btn) {
      btn.setAttribute('aria-disabled', 'true');
      btn.disabled = true;
      const t = btn.querySelector('[data-add-text]');
      if (t) t.textContent = 'Unavailable';
    }
  }

  async renderSection(variantId) {
    try {
      const res = await fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      ['[data-price-block]', '[data-buy-block]', '[data-inventory-block]', '[data-sku-block]']
        .forEach(sel => {
          const incoming = html.querySelector(sel);
          const current = document.querySelector(sel);
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        });

      // Sync sticky buy bar
      const barPrice = document.querySelector('[data-buybar-price]');
      const newPrice = html.querySelector('[data-price-block] .dw-price-current');
      if (barPrice && newPrice) barPrice.textContent = newPrice.textContent.trim();
    } catch (err) {
      console.error('[DreamWood] variant render failed', err);
    }
  }
}
customElements.define('dw-variants', DWVariants);

/* ==========================================================================
   Engraving
   ========================================================================== */
class DWEngrave extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-engrave-toggle]');
    this.fields = this.querySelector('[data-engrave-fields]');
    this.input = this.querySelector('[data-engrave-input]');
    this.count = this.querySelector('[data-engrave-count]');
    this.preview = this.querySelector('[data-engrave-preview]');
    this.max = parseInt(this.dataset.max || '30', 10);

    this.toggle?.addEventListener('change', () => {
      this.fields.hidden = !this.toggle.checked;
      if (!this.toggle.checked && this.input) {
        this.input.value = '';
        this.sync();
      }
    });
    this.input?.addEventListener('input', () => this.sync());
    this.sync();
  }
  sync() {
    if (!this.input) return;
    const val = this.input.value.slice(0, this.max);
    this.input.value = val;
    if (this.count) this.count.textContent = `${val.length} / ${this.max}`;
    if (this.preview) this.preview.textContent = val || 'Your engraving appears here';
  }
}
customElements.define('dw-engrave', DWEngrave);

/* ==========================================================================
   Cart
   ========================================================================== */
class DWCart {
  static async add(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }
  static async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return res.json();
  }
  static async get() {
    const res = await fetch('/cart.js');
    return res.json();
  }
  static async refresh() {
    const cart = await DWCart.get();
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    const drawer = document.getElementById('dw-cart-drawer');
    if (drawer) {
      const res = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const incoming = html.querySelector('[data-cart-contents]');
      const current = drawer.querySelector('[data-cart-contents]');
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    }
    return cart;
  }
}
window.DWCart = DWCart;

/* Add to cart */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-product-form]');
  if (!form) return;
  e.preventDefault();

  const btn = form.querySelector('[type="submit"]');
  const text = btn?.querySelector('[data-add-text]');
  const original = text?.textContent;
  if (btn) { btn.disabled = true; }
  if (text) text.textContent = 'Adding…';

  try {
    await DWCart.add(new FormData(form));
    document.dispatchEvent(new CustomEvent('dw:added-to-cart'));
    await DWCart.refresh();
    document.getElementById('dw-cart-drawer')?.open();
    if (text) text.textContent = original;
  } catch (err) {
    DW.toast(err.message);
    if (text) text.textContent = original;
  } finally {
    if (btn) btn.disabled = false;
  }
});

/* Cart quantity */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-qty-change]');
  if (!btn) return;
  const line = parseInt(btn.dataset.line, 10);
  const delta = parseInt(btn.dataset.qtyChange, 10);
  const input = btn.parentElement.querySelector('input');
  const next = Math.max(0, parseInt(input.value, 10) + delta);
  input.value = next;
  await DWCart.change(line, next);
  await DWCart.refresh();
});

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-cart-remove]');
  if (!btn) return;
  e.preventDefault();
  await DWCart.change(parseInt(btn.dataset.line, 10), 0);
  await DWCart.refresh();
});

/* ==========================================================================
   Sticky buy bar
   ========================================================================== */
(function () {
  const bar = document.querySelector('.dw-buybar');
  const anchor = document.querySelector('[data-buy-block]');
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(([entry]) => {
    bar.classList.toggle('is-in', !entry.isIntersecting && entry.boundingClientRect.top < 0);
  }, { threshold: 0 });
  io.observe(anchor);
})();

/* ==========================================================================
   Predictive search
   ========================================================================== */
class DWSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    let timer;
    this.input?.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.run(), 260);
    });
  }
  async run() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.results.innerHTML = ''; return; }
    try {
      const res = await fetch(
        `/search/suggest?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=6&section_id=dw-predictive-search`
      );
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const incoming = html.querySelector('[data-search-results]');
      if (incoming) this.results.innerHTML = incoming.innerHTML;
    } catch (err) {
      console.error('[DreamWood] search failed', err);
    }
  }
}
customElements.define('dw-search', DWSearch);

/* ==========================================================================
   Size guide toggle
   ========================================================================== */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-sizehelp-toggle]');
  if (!btn) return;
  const panel = document.getElementById(btn.dataset.sizehelpToggle);
  if (panel) {
    panel.hidden = !panel.hidden;
    btn.textContent = panel.hidden ? 'Find my size' : 'Hide sizing help';
  }
});

/* ==========================================================================
   Resize Coverage — product-page add-on
   The checkbox lives on the PDP but the line item is added when the ring is,
   so the two always travel together.
   ========================================================================== */
(function () {
  const KEY = 'dw-coverage-wanted';

  // restore the checkbox state within the session
  document.addEventListener('DOMContentLoaded', () => {
    const box = document.querySelector('[data-coverage-toggle]');
    if (box && !box.checked && sessionStorage.getItem(KEY) === '1') box.checked = true;
  });

  document.addEventListener('change', (e) => {
    const box = e.target.closest('[data-coverage-toggle]');
    if (!box) return;
    sessionStorage.setItem(KEY, box.checked ? '1' : '0');
    const note = box.closest('[data-resize-coverage]')?.querySelector('[data-coverage-status]');
    if (note) note.textContent = box.checked ? 'Coverage will be added with this ring.' : '';
  });

  // after the ring is added, add coverage too
  document.addEventListener('dw:added-to-cart', async () => {
    const box = document.querySelector('[data-coverage-toggle]');
    if (!box || !box.checked) return;
    try {
      const cart = await DWCart.get();
      const already = cart.items.some(i => String(i.variant_id) === String(box.dataset.variantId));
      if (already) return;
      const fd = new FormData();
      fd.append('id', box.dataset.variantId);
      fd.append('quantity', 1);
      await DWCart.add(fd);
      await DWCart.refresh();
    } catch (err) {
      console.error('[DreamWood] coverage add failed', err);
    }
  });
})();
